import { useEffect, useState } from 'react'
import AdminLayout from '@/components/AdminLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { fetchAllRows } from '@/utils/supabasePagination'
import { useNavigate } from 'react-router-dom'
import { Calendar, LayoutGrid, ListIcon, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface Test {
  id: string
  name: string
  created_at: string
  time_limit: number
  categories: any
}

const AdminResults = () => {
  const navigate = useNavigate()
  const [tests, setTests] = useState<Test[]>([])
  const [loading, setLoading] = useState(true)
  const [testSearchInput, setTestSearchInput] = useState('')
  const [testSearch, setTestSearch] = useState('')
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card')

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(9)

  useEffect(() => {
    const fetchTests = async () => {
      try {
        const data = await fetchAllRows<Test>(
          'tests',
          'id, name, categories, time_limit, created_at',
          (q) => q.order('created_at', { ascending: false })
        )
        setTests(data)
      } catch (error) {
        console.log('error', error)
      } finally {
        setLoading(false)
      }
    }
    fetchTests()
  }, [])

  const filteredTests = tests.filter((test) =>
    test.name.toLocaleLowerCase('tr').includes(testSearch.toLocaleLowerCase('tr'))
  )

  // Pagination Logic
  const totalPages = Math.ceil(filteredTests.length / itemsPerPage)
  const paginatedTests = filteredTests.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  const formatDate = (isoDate?: string | null) => {
    if (!isoDate) return ''

    const date = new Date(isoDate)
    if (isNaN(date.getTime())) return ''

    // UTC+3
    date.setHours(date.getHours() + 3)

    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const handleSearch = () => {
    setTestSearch(testSearchInput)
    setCurrentPage(1)
  }

  // Reset page on items-per-page change
  useEffect(() => {
    setCurrentPage(1)
  }, [itemsPerPage])

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-wrap justify-between items-center gap-2">
          <h1 className="text-2xl font-bold">Sınav Sonuçları</h1>
        </div>

        <div className="flex justify-between items-center gap-4 mb-8 w-full">
          <div className="flex items-center gap-2 w-full max-w-md">
            <Input
              placeholder="Sınav adıyla ara..."
              value={testSearchInput}
              onChange={(e) => setTestSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full"
            />
            <Button onClick={handleSearch} variant="outline" className="shrink-0">
              <Search className="h-4 w-4 mr-1" />
              Ara
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'card' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('card')}
            >
              <LayoutGrid className="h-4 w-4 mr-1" />
              Kart
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <ListIcon className="h-4 w-4 mr-1" />
              Liste
            </Button>
            <div className="flex items-center gap-2 ml-4">
              <span className="text-sm text-gray-500">Sayfa başı:</span>
              <Select
                value={itemsPerPage.toString()}
                onValueChange={(value) => setItemsPerPage(Number(value))}
              >
                <SelectTrigger className="w-24">
                  <SelectValue placeholder="9" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">6</SelectItem>
                  <SelectItem value="9">9</SelectItem>
                  <SelectItem value="15">15</SelectItem>
                  <SelectItem value="30">30</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center p-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : filteredTests.length > 0 ? (
          viewMode === 'card' ? (
            // CARD VIEW
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedTests.map((test) => (
                <Card
                  key={test.id}
                  className="cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => navigate(`/admin/results/${test.id}`)}
                >
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      {test.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-500">
                      Oluşturulma: {formatDate(test.created_at)}
                    </p>
                    <div className="mt-4">
                      <Button className="w-full">Sonuçları Görüntüle</Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            // LIST VIEW
            <div className="overflow-x-auto">
              <div className="rounded-2xl shadow-lg border border-gray-200 bg-white overflow-hidden my-4">
                <table className="min-w-full bg-white">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-4 text-left rounded-tl-2xl">Sınav Adı</th>
                      <th className="p-4 text-left">Oluşturulma</th>
                      <th className="p-4 rounded-tr-2xl"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTests.map((test, idx) => (
                      <tr
                        key={test.id}
                        className={
                          'hover:bg-gray-50 cursor-pointer transition ' +
                          (idx === paginatedTests.length - 1
                            ? 'border-b-0'
                            : 'border-b border-gray-100')
                        }
                        onClick={() => navigate(`/admin/results/${test.id}`)}
                      >
                        <td className="p-4">{test.name}</td>
                        <td className="p-4">{formatDate(test.created_at)}</td>
                        <td className="p-4 text-right">
                          <Button size="sm" variant="outline">
                            Sonuçları Görüntüle
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500">Sınav bulunamadı.</p>
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
            >
              Önceki
            </Button>
            {[...Array(totalPages)].map((_, idx) => (
              <Button
                key={idx}
                variant={currentPage === idx + 1 ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCurrentPage(idx + 1)}
              >
                {idx + 1}
              </Button>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Sonraki
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}

export default AdminResults
